import { Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { SquarePenIcon } from "lucide-react";
import React from "react";

import { trpc } from "@blis/trpc/react";

import { BarChart } from "@/components/charts/bar-chart";
import { DonutChart } from "@/components/charts/donut-chart";
import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { Link as BlisLink } from "@/components/Link";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
} from "@/components/ui/empty-state";
import { StatusIcon } from "@/components/ui/icons";
import { ListItem } from "@/components/ui/list-item";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import { useAuth } from "@/features/auth/hooks";
import { useView } from "@/features/view/components/ViewProvider";
import type { TAccession } from "@/lib/types";
import { useNavLocationState } from "@/utils/useNavStack";
import type { TComment } from "../types";

export function HomeDashboard() {
  return (
    <div className="overflow-auto flex-1 p-4">
      <div className="mb-6 grid grid-cols-1 gap-x-0 gap-y-6 lg:grid-cols-3 lg:gap-6">
        <Card>
          <CardHeader>Active</CardHeader>
          <CardContent className="@container">
            <ActiveChart />
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardHeader>Activity</CardHeader>
          <CardContent>
            <VolumeChart />
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
        <MyAccessionsCard />
        <RecentCommentsCard />
        <AccessionsWithHoldCard />
      </div>
    </div>
  );
}

function ActiveChart() {
  const { data } = trpc.metrics.status.useQuery();
  const { setAdditionalFilters, resetFilters } = useView();

  const sorted = (
    [
      { id: "new", label: "New", value: 0, color: "bg-gray-300" },
      { id: "received", label: "Received", value: 0, color: "bg-gray-500" },
      {
        id: "in_progress",
        label: "In progress",
        value: 0,
        color: "bg-amber-500",
      },
      {
        id: "completed",
        label: "Completed",
        value: 0,
        color: "bg-emerald-500",
      },
    ] as const
  ).map((item) => ({
    ...item,
    value: data?.find((d) => d.id === item.id)?.value || 0,
  }));

  return (
    <div className="grid grid-cols-1 gap-x-0 gap-y-4 sm:grid-cols-2 sm:gap-8">
      <DonutChart
        data={sorted}
        className="w-full h-40 my-auto"
        category="id"
        value="value"
        colors={["lightGray", "gray", "amber", "emerald"]}
        showLabel
        valueFormatter={(value) => value.toString()}
      />
      <div className="flex items-center sm:mt-0 w-full">
        <div className="grid grid-cols-1 gap-0 @sm:gap-1 @sm:grid-cols-2 w-full">
          {sorted.map((item) => (
            <Link
              key={item.id}
              to="/accessions"
              className="flex space-x-3 w-full p-1 rounded-md border border-transparent hover:border-border"
              onClick={() => {
                resetFilters();
                setAdditionalFilters((s) => ({ ...s, status: [item.id] }));
              }}
            >
              <span className={cn(item.color, "w-1 shrink-0 rounded")} />
              <div className="flex flex-col leading-none">
                <span className="font-medium">{item.value}</span>
                <span className="whitespace-nowrap text-muted-foreground text-sm">
                  {item.label}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function VolumeChart() {
  const { data } = trpc.metrics.volume.useQuery({ days: 60 });

  const formatted =
    data?.map((item) => ({
      ...item,
      day: dayjs(item.day).format("MMM D"),
      Accessions: item.value,
    })) || [];

  return (
    <BarChart
      className="h-40"
      data={formatted}
      index="day"
      categories={["Accessions"]}
      allowDecimals={false}
    />
  );
}

function MyAccessionsCard() {
  const { data, isLoading } = trpc.accession.list.all.useQuery({
    filter: { myAccessions: true },
    take: 10,
  });
  const navigate = useNavigate();
  const { setAdditionalFilters, resetFilters } = useView();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex justify-between">
          My accessions
          {data && data.length > 0 && (
            <BlisLink
              to="/accessions"
              params={{}}
              onPress={() => {
                resetFilters();
                setAdditionalFilters((s) => ({ ...s, myAccessions: true }));
              }}
            >
              <Text size="1">View more &rarr;</Text>
            </BlisLink>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <NavList
          items={data || []}
          isLoading={isLoading}
          aria-label="My accessions"
          emptyState={
            <EmptyState>
              <EmptyStateDescription>
                No pending accessions
              </EmptyStateDescription>
              <EmptyStateActions>
                <Button
                  onPress={() => navigate({ to: "/accessions/new" })}
                  variant="outline"
                >
                  <SquarePenIcon data-slot="icon" /> New accession
                </Button>
              </EmptyStateActions>
            </EmptyState>
          }
        >
          {(item) => (
            <NavListItem key={item.id.toString()} asChild>
              <DashboardAccessionItem accession={item} />
            </NavListItem>
          )}
        </NavList>
      </CardContent>
    </Card>
  );
}

function AccessionsWithHoldCard() {
  const { data, isLoading } = trpc.accession.list.all.useQuery({
    filter: {
      holdStatus: ["active"],
      status: ["new", "received", "in_progress", "completed"],
    },
    take: 10,
  });
  const { setAdditionalFilters, resetFilters } = useView();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex justify-between">
          Active hold
          {data && data.length > 0 && (
            <BlisLink
              to="/accessions"
              params={{}}
              onPress={() => {
                resetFilters();
                setAdditionalFilters((s) => ({ ...s, holdStatus: ["active"] }));
              }}
            >
              <Text size="1">View more &rarr;</Text>
            </BlisLink>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <NavList
          items={data || []}
          aria-label="Active holds"
          isLoading={isLoading}
          emptyState={
            <EmptyState>
              <EmptyStateDescription>No active holds</EmptyStateDescription>
            </EmptyState>
          }
        >
          {(item) => (
            <NavListItem key={item.id.toString()} asChild>
              <DashboardAccessionItem accession={item} />
            </NavListItem>
          )}
        </NavList>
      </CardContent>
    </Card>
  );
}

function RecentCommentsCard() {
  const { data } = trpc.metrics.recentComments.useQuery({});
  const comments = data || [];

  return (
    <Card className="row-start-3 lg:row-span-2">
      <CardHeader>
        <CardTitle className="inline-flex justify-between">
          Recent comments
          {comments.length > 0 && (
            <BlisLink to="/comments" params={{}}>
              <Text size="1">View all &rarr;</Text>
            </BlisLink>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <DashboardCommentItem key={comment.id} comment={comment} />
          ))
        ) : (
          <EmptyState>
            <EmptyStateDescription>No recent comments</EmptyStateDescription>
          </EmptyState>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardCommentItem({ comment }: { comment: TComment }) {
  const { user } = useAuth();
  const commenter =
    comment.createdById === user?.profile.id
      ? "You"
      : comment.createdBy?.username || "Someone";
  const daysAgo = dayjs(comment.createdAt).fromNow();
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();

  return (
    <div className="flex gap-2 timeline-item">
      <UserAvatar user={comment.createdBy} className="mt-1" />
      <Card
        className="flex-1 cursor-pointer hover:shadow-xs"
        onClick={() => {
          navigate({
            to: "/accessions/$id",
            params: { id: comment.accessionId },
            state: { href, selected: comment.accessionId, label },
          });
        }}
      >
        <CardContent className="py-2 px-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Text bold>{commenter}</Text>{" "}
              <Text size="1" muted>
                {daysAgo}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <Badge>{comment.accessionId}</Badge>
            </div>
          </div>
          <Text
            size="1"
            elementType="p"
            className="whitespace-pre-wrap line-clamp-3"
          >
            {comment.text}
          </Text>
        </CardContent>
      </Card>
    </div>
  );
}

const DashboardAccessionItem = React.forwardRef<
  HTMLAnchorElement,
  { accession: TAccession }
>(({ accession, ...props }, ref) => {
  const { href, label } = useNavLocationState();

  return (
    <ListItem {...props} asChild>
      <Link
        ref={ref}
        to="/accessions/$id"
        params={{ id: accession.id }}
        state={{ href, selected: accession.id, label }}
        className="flex items-center justify-between p-2 cursor-pointer h-full outline-hidden"
      >
        <div className="flex items-center gap-3 flex-1 overflow-hidden">
          <Badge>{accession.id}</Badge>
          <StatusIcon status={accession.status || "new"} />
          <Text truncate>{accession.caseId}</Text>
          {accession.hasHold && <ActiveHoldIndicator />}
          {accession.priority === "STAT" && <StatIndicator />}
        </div>
        <div className="flex items-center gap-3">
          <Text mono size="1" muted>
            {dayjs(accession.date).fromNow()}
          </Text>
        </div>
      </Link>
    </ListItem>
  );
});

function _findFirstSundayAfterDays(startDays, endDays) {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - startDays);

  const endDate = new Date();
  endDate.setDate(endDate.getDate() - endDays);

  while (startDate <= endDate) {
    if (startDate.getDay() === 6) {
      // 0 represents Sunday in the JS Date object
      return startDate;
    }
    startDate.setDate(startDate.getDate() + 1);
  }

  return null;
}
